import { ScanText } from "lucide-react"
import { useEffect, useState } from "react"

/**
 * Lo que se ve mientras Gemini lee una factura: 10–30 segundos, a veces más.
 *
 * ⚠ **Un botón quieto que dice «Leyendo…» durante medio minuto parece una pantalla
 * colgada**, y lo que hace la gente con una pantalla colgada es recargarla —perdiendo
 * la lectura— o pulsar otra vez. Aquí hay cuatro señales de vida: la barra avanza, el
 * texto cambia de etapa, los segundos corren y el icono late.
 *
 * ⚠ **La barra NO sabe cuánto falta, y no lo finge.** Se acerca al 90 % sin llegar
 * nunca —`1 − e^(−t/12)`: la mitad a los 8 s, el 80 % a los 25 s— y sólo se llena cuando
 * llega la respuesta, porque entonces este componente deja de pintarse. Una barra que
 * llega al 100 % y se queda ahí es peor que no tener barra: dice «listo» y no lo está.
 *
 * ⚠ **Sólo se anima `transform` y `opacity`** (reglas del frontend), y el latido del
 * icono respeta `prefers-reduced-motion`: con él apagado, la barra y el texto siguen
 * diciendo que algo ocurre.
 */

/** Etapas por tiempo transcurrido. Aproximadas: describen lo que suele estar pasando. */
const ETAPAS: { desde: number; texto: string }[] = [
  { desde: 0, texto: "Enviando la factura…" },
  { desde: 3, texto: "Leyendo el documento…" },
  { desde: 9, texto: "Transcribiendo renglones, cantidades y montos…" },
  { desde: 20, texto: "Verificando que las cuentas cuadren…" },
  {
    desde: 35,
    texto: "Sigue trabajando: el modelo gratuito a veces va lento o está saturado…",
  },
]

/** Las del comprobante de un pago: una sola imagen y pocos datos, suele ser más rápido. */
const ETAPAS_COMPROBANTE: { desde: number; texto: string }[] = [
  { desde: 0, texto: "Enviando el comprobante…" },
  { desde: 3, texto: "Leyendo monto, fecha y referencia…" },
  { desde: 12, texto: "Comparando con lo pendiente de la orden…" },
  {
    desde: 30,
    texto: "Sigue trabajando: el modelo gratuito a veces va lento o está saturado…",
  },
]

/** Los de una persona: cédula, RIF y currículo, varios archivos en un viaje. */
const ETAPAS_PERSONAL: { desde: number; texto: string }[] = [
  { desde: 0, texto: "Enviando los documentos…" },
  { desde: 3, texto: "Identificando cuál es la cédula, el RIF y el currículo…" },
  { desde: 10, texto: "Transcribiendo datos personales, estudios y experiencia…" },
  { desde: 22, texto: "Comprobando que todos sean de la misma persona…" },
  {
    desde: 38,
    texto: "Sigue trabajando: el modelo gratuito a veces va lento o está saturado…",
  },
]

export type Documento = "factura" | "comprobante" | "personal"

const ETAPAS_DE: Record<Documento, { desde: number; texto: string }[]> = {
  factura: ETAPAS,
  comprobante: ETAPAS_COMPROBANTE,
  personal: ETAPAS_PERSONAL,
}

/** Hasta dónde llega la barra a los `segundos`. Nunca pasa de 0,9. */
export function avance(segundos: number): number {
  return 0.9 * (1 - Math.exp(-segundos / 12))
}

export function etapaEn(segundos: number, documento: Documento = "factura"): string {
  const etapas = ETAPAS_DE[documento]
  let actual = etapas[0]?.texto ?? ""
  for (const etapa of etapas) if (segundos >= etapa.desde) actual = etapa.texto
  return actual
}

const ROTULO: Record<Documento, string> = {
  factura: "Lectura de la factura",
  comprobante: "Lectura del comprobante",
  personal: "Lectura de los documentos",
}

export function ProgresoLectura({
  archivos,
  documento = "factura",
}: {
  archivos: number
  documento?: Documento
}) {
  const [segundos, setSegundos] = useState(0)

  useEffect(() => {
    const inicio = performance.now()
    const reloj = window.setInterval(() => setSegundos((performance.now() - inicio) / 1000), 250)
    return () => window.clearInterval(reloj)
  }, [])

  const porcentaje = Math.round(avance(segundos) * 100)

  return (
    <div className="tarjeta space-y-3 p-3 sm:p-4">
      <div className="flex items-center gap-3">
        <ScanText
          size={20}
          aria-hidden={true}
          className="shrink-0 motion-safe:animate-pulse"
          style={{ color: "var(--primary)" }}
        />
        <p className="flex-1 text-sm font-medium" aria-live="polite">
          {etapaEn(segundos, documento)}
        </p>
        <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {Math.floor(segundos)} s
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={ROTULO[documento]}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={porcentaje}
        className="h-1.5 overflow-hidden rounded-full"
        style={{ backgroundColor: "var(--cristal-filo)" }}
      >
        {/* `scaleX` y no `width`: sólo se anima `transform` (reglas del frontend). */}
        <div
          className="h-full w-full origin-left rounded-full transition-transform duration-200 ease-linear"
          style={{ backgroundColor: "var(--primary)", transform: `scaleX(${avance(segundos)})` }}
        />
      </div>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {archivos === 1 ? "1 archivo" : `${archivos} archivos`} · suele tardar entre 10 y 30
        segundos. No cierres esta ventana.
      </p>
    </div>
  )
}
